// Álbum: quais cards a trilha já entregou ao aluno. Função pura, sem React e
// sem conhecer o AVA.
//
// DERIVADO das fases concluídas cruzadas com o sorteio guardado — não da lista
// `cardsColetados` do navegador. Dois motivos:
//
// 1. `cardsColetados` só é escrita quando o aluno FECHA a cerimônia. Recarregar
//    a página com o modal aberto, ou sair da atividade no meio da revelação,
//    perde o card para sempre, embora a fase conste como concluída no servidor.
//    O álbum ficaria com menos cards do que a trilha entregou.
// 2. A fonte de verdade do progresso é o servidor. Uma segunda lista, escrita
//    por outro caminho, pode divergir do tabuleiro — e aí a mesma trilha diz
//    duas coisas diferentes sobre o que o aluno fez.
//
// Derivar não pode divergir: se a fase está concluída e ela premia, o card está
// no álbum.

import { recompensaDaFase } from "./recompensaDaFase";
import { distribuicaoDeRecompensas } from "./recompensas";

/**
 * Cards já conquistados, em ordem de fase.
 *
 * SÓ AS CARTAS, e desde 24/08/2026 isso exclui a fase rara que não deu carta: o
 * arco aconteceu (a cena, a fala do mascote), mas não há colecionável para o álbum
 * nem para o servidor. Quem cruza a fase com o sorteio é `recompensaDaFase`, e
 * derivar daqui é o que impede o álbum de mostrar carta que o aluno não ganhou.
 *
 * @param {Array}  fases       [{ numero, concluida }]
 * @param {number} totalFases
 * @param {object} conteudo    { cardsComuns, cardsRaros }
 * @param {object} sorteio     { comuns, raras } (ver recompensaDaFase)
 * @returns {Array} cards, sem repetição
 */
export function cardsDoAlbum(
  fases = [],
  totalFases,
  conteudo = {},
  sorteio = {}
) {
  const vistos = new Set();

  return fases
    .filter(fase => fase && fase.concluida)
    .map(fase => fase.numero)
    .sort((a, b) => a - b)
    .map(numero => recompensaDaFase(numero, totalFases, conteudo, sorteio))
    .filter(card => {
      // `recompensaDaFase` repete o último card raro se houver mais fases raras
      // que cards raros — no álbum isso apareceria como carta duplicada.
      if (!card || vistos.has(card.id)) return false;
      vistos.add(card.id);
      return true;
    });
}

/**
 * Quantos cards a trilha entrega do começo ao fim, para ESTE sorteio.
 *
 * TUDO vem do sorteio desde 24/08/2026: as comuns são as chaves do mapa (a posição
 * passou a ser sorteada, então a geometria não sabe mais quantas são) e as raras,
 * as que a moeda contemplou. Sem sorteio o total é ZERO — o mesmo "errar para o
 * lado de não entregar" de `recompensaDaFase`, e o único valor coerente: um
 * denominador de 4 cartas num álbum que só pode mostrar as do mapa faria o
 * encerramento anunciar "2 de 4" numa trilha que entregou 2 de 2.
 *
 * SÓ AS CHAVES DENTRO DA TRILHA, e isso não é paranoia: o mapa guardado congela o
 * sorteio de quando a trilha foi aberta, e o professor pode REMOVER itens depois.
 * Contando o mapa cru, uma trilha que encolheu de 22 para 8 fases anunciaria um
 * total que o aluno nunca alcança — o encerramento pararia em "3 de 5" para sempre.
 * Contar a geometria não protegia disso porque a geometria era recalculada; contar
 * o mapa protege, desde que se ignore o que caiu fora do fim.
 */
export function totalDeCardsDaTrilha(totalFases, sorteio = {}) {
  const { raras } = distribuicaoDeRecompensas(totalFases);
  const comuns = Object.keys(sorteio.comuns || {}).filter(chave => {
    const fase = Number(chave);
    return fase >= 1 && fase <= totalFases;
  });
  const sorteadas = raras.filter(fase => sorteio.raras && sorteio.raras[fase]);
  return comuns.length + sorteadas.length;
}
